/**
 * Tiled map JSON helpers (property normalization) and world-space vertices for Tiled objects (nav, etc.).
 * Previously lived under game-engine-service; consolidated here for reuse without pulling in GES.
 */

import type { ITiledObject } from '@nova/types';

import { GeomUtils, type ICoord2D } from './geom-utils';

export function convertTiledPropsToSimpleKVPair(properties: any) {
  const retObj: { [name: string]: any } = {};

  // Guard against null, undefined, or non-iterable values
  if (!properties || !Array.isArray(properties)) {
    return retObj;
  }

  for (const prop of properties) {
    retObj[prop.name] = prop.value;
  }

  return retObj;
}

export function convertTiledNamedArrayToSimpleKVPair(namedArr: any[]) {
  const retObj: { [name: string]: any } = {};

  // Guard against null, undefined, or non-iterable values
  if (!namedArr || !Array.isArray(namedArr)) {
    return retObj;
  }

  for (const obj of namedArr) {
    const { name, ...rest } = obj;
    retObj[name] = rest;
  }

  return retObj;
}

export function convertTiledJsonToSimpleKVPairs(refJson: any) {
  if (!refJson?.layers) { return; }

  // Tiled exports `layers` as an array; first pass replaces it with a name → layer map. If the same map is processed again
  // without cloning, `layers` is already an object — ConvertTiledNamedArrayToSimpleKVPair would return {} and wipe the map.
  let layers: Record<string, any>;
  if (Array.isArray(refJson.layers)) {
    layers = convertTiledNamedArrayToSimpleKVPair(refJson.layers);
  }
  else {
    layers = refJson.layers;
  }

  for (const layerName in layers) {
    if (!Object.prototype.hasOwnProperty.call(layers, layerName)) { continue; }
    const layer = layers[layerName];

    if (layer.objects) {
      // * Skipping over objects for this process, as they are far more likely to have repeated names,
      // * whereas layers could but shouldn't, and properties cannot ever.
      const objects = layer.objects; // ConvertTiledNamedArrayToSimpleKVPair(layer['objects']);

      for (const objectName in objects) {
        if (!Object.prototype.hasOwnProperty.call(objects, objectName)) { continue; }
        const object = objects[objectName];

        if (object.properties && Array.isArray(object.properties)) {
          const props = convertTiledPropsToSimpleKVPair(object.properties);
          object.properties = props;
        }
      }

      layer.objects = objects;
    }
  }

  refJson.layers = layers;
}

export function findMainNavTiledObject(navObjects: ITiledObject[]): ITiledObject | undefined {
  return navObjects.find((o) => !!o.properties?.main);
}

function getTiledObjectWorldVerticesInternal(
  collObj: ITiledObject,
  roundToInts: boolean | undefined,
  logInvalidShape: boolean
): ICoord2D[] {
  const vertices: ICoord2D[] = [];

  if (collObj.polygon) {
    for (let index = 0; index < collObj.polygon.length; index++) {
      const vertex = collObj.polygon[index];
      vertices.push({
        x: roundToInts ? Math.round(collObj.x + vertex.x) : collObj.x + vertex.x,
        y: roundToInts ? Math.round(collObj.y + vertex.y) : collObj.y + vertex.y
      });
    }
    return vertices;
  }

  if (collObj.width && collObj.height) {
    const rectVerts = GeomUtils.GetTiledRectangleWorldVertices(
      { x: collObj.x, y: collObj.y },
      collObj.width,
      collObj.height,
      collObj.rotation || 0
    );
    for (const v of rectVerts) {
      vertices.push({
        x: roundToInts ? Math.round(v.x) : v.x,
        y: roundToInts ? Math.round(v.y) : v.y
      });
    }
    return vertices;
  }

  if (logInvalidShape) {
    // eslint-disable-next-line no-console
    console.error('ONLY POLYGONS & RECTANGLES for NavMesh allowed.');
  }
  return [];
}

/** World vertices for a Tiled polygon or rectangle object (nav holes, etc.). */
export function getTiledObjectWorldVertices(collObj: ITiledObject, roundToInts?: boolean): ICoord2D[] {
  return getTiledObjectWorldVerticesInternal(collObj, roundToInts, true);
}

/** Vertices for the nav object marked `main`, or null if missing / not a polygon or rectangle. */
export function getMainNavPlaneVertices(navObjects: ITiledObject[]): ICoord2D[] | null {
  const main = findMainNavTiledObject(navObjects);
  if (!main) { return null; }
  const verts = getTiledObjectWorldVerticesInternal(main, false, false);
  return verts.length > 0 ? verts : null;
}
